// src/pages/atributos/NuevoValorAtributo.js
import React, { useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Badge from 'react-bootstrap/Badge'
import InputGroup from 'react-bootstrap/InputGroup'

function NuevoValorAtributo({ mostrar, tipo, guardar, cancelar }) {
    const [valores, setValores] = useState([])
    const [nuevoValor, setNuevoValor] = useState('')
    const [errores, setErrores] = useState({})
    const [loading, setLoading] = useState(false)

    const handleClose = () => {
        resetForm()
        cancelar()
    }

    const resetForm = () => {
        setValores([])
        setNuevoValor('')
        setErrores({})
    }

    const agregarValor = () => {
        const valorTrimmed = nuevoValor.trim()

        if (!valorTrimmed) return

        // Verificar que no exista ya en los nuevos valores
        if (valores.includes(valorTrimmed)) {
            setErrores({ nuevoValor: 'Este valor ya está en la lista' })
            return
        }

        // Verificar que no exista en los valores existentes del tipo
        if (tipo.valores?.some(v => v.nombre.toLowerCase() === valorTrimmed.toLowerCase())) {
            setErrores({ nuevoValor: 'Este valor ya existe en el tipo de atributo' })
            return
        }

        setValores([...valores, valorTrimmed])
        setNuevoValor('')
        setErrores({})
    }

    const eliminarValor = (index) => {
        setValores(valores.filter((_, i) => i !== index))
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            agregarValor()
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (valores.length === 0) {
            setErrores({ general: 'Debe agregar al menos un valor' })
            return
        }

        setLoading(true)

        try {
            // Si es un solo valor, enviarlo directamente
            // Si son múltiples, enviarlos como array
            if (valores.length === 1) {
                await guardar({
                    nombre: valores[0],
                    activo: true
                })
            } else {
                await guardar({
                    valores: valores.map((v, index) => ({
                        nombre: v,
                        orden: (tipo.valores?.length || 0) + index + 1,
                        activo: true
                    }))
                })
            }
            resetForm()
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    if (!tipo) return null

    return (
        <Modal show={mostrar} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-plus-circle text-success me-2"></i>
                    Agregar valores
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <div className="mb-3 p-3 bg-light rounded">
                        <small className="text-muted">
                            Agregando valores para: <strong>{tipo.nombre}</strong>
                        </small>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>Nuevo valor</Form.Label>
                        <InputGroup>
                            <Form.Control
                                type="text"
                                value={nuevoValor}
                                onChange={(e) => {
                                    setNuevoValor(e.target.value)
                                    setErrores({})
                                }}
                                onKeyPress={handleKeyPress}
                                placeholder="Escribir valor y presionar Enter"
                                isInvalid={!!errores.nuevoValor}
                                autoFocus
                            />
                            <Button
                                variant="outline-primary"
                                onClick={agregarValor}
                                disabled={!nuevoValor.trim()}
                            >
                                <i className="fa fa-plus"></i>
                            </Button>
                        </InputGroup>
                        {errores.nuevoValor && (
                            <Form.Text className="text-danger">
                                {errores.nuevoValor}
                            </Form.Text>
                        )}
                        <Form.Text className="text-muted">
                            Puede agregar múltiples valores antes de guardar
                        </Form.Text>
                    </Form.Group>

                    {errores.general && (
                        <div className="alert alert-danger">
                            <i className="fa fa-exclamation-circle me-1"></i>
                            {errores.general}
                        </div>
                    )}

                    {valores.length > 0 && (
                        <div className="valores-preview mt-3">
                            <small className="text-muted d-block mb-2">
                                Valores a agregar ({valores.length}):
                            </small>
                            <div className="d-flex flex-wrap gap-2">
                                {valores.map((valor, index) => (
                                    <Badge
                                        key={index}
                                        bg="success"
                                        className="valor-badge d-flex align-items-center"
                                    >
                                        {valor}
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 ms-2 text-white"
                                            onClick={() => eliminarValor(index)}
                                        >
                                            <i className="fa fa-times"></i>
                                        </Button>
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {tipo.valores && tipo.valores.length > 0 && (
                        <div className="mt-4">
                            <small className="text-muted d-block mb-2">
                                Valores existentes ({tipo.valores.length}):
                            </small>
                            <div className="d-flex flex-wrap gap-1">
                                {tipo.valores.map((valor) => (
                                    <Badge
                                        key={valor.id}
                                        bg="secondary"
                                        className={!valor.activo ? 'opacity-50' : ''}
                                    >
                                        {valor.nombre}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleClose} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        type="submit"
                        disabled={loading || valores.length === 0}
                    >
                        {loading ? (
                            <>
                                <i className="fa fa-spinner fa-spin me-2"></i>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save me-2"></i>
                                Guardar {valores.length > 1 ? `${valores.length} valores` : 'valor'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    )
}

export default NuevoValorAtributo